import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout/Layout';
import FeatureValuePropsBlockNew from '../components/FeatureBlocks/FeatureValuePropsBlock/FeatureValuePropsBlockNew';
import AiBlock from '../components/AiBlock/AiBlock';
import OverlapCarouselBlock from '../components/FeatureBlocks/OverlapCarouselBlock/OverlapCarouselBlock';
import FeatureChecklistHeroNew from '../components/Hero/FeatureChecklistHero/FeatureChecklistHero';
import TestimonialCarouselCardBlock from '../components/FeatureBlocks/TestimonialCarouselCardBlock/TestimonialCarouselCardBlock';
import PromoBlockLeft from '../components/FeatureBlocks/PromoBlockLeft/PromoBlockLeft';
import { TAdditionalDataForClickStartTrialModules } from '../types/util-types';
import RotatingTitleBlock from '../components/FeatureBlocks/RotatingTitleBlock/RotatingTitleBlock';
import verticalPaidStore from '../stores/verticalPaidStore';
import { usePromoCode } from '../hooks/usePromoCode';
import { useABTestContext } from '../ab-tests/context/useABTestContext';
import { useMemo, useRef, useEffect } from 'react';
import BroadIntentContent from '../components/BroadIntentContent/BroadIntentContent';
import AnalyticsService, {
    AnalyticsEvents
} from '../Services/AnalyticsService';

import '../styles/paid-marketing.scss';
import { WindowLocation } from '@reach/router';

const ogTags = {
    url: 'https://www.honeybook.com/getstarted',
    title: 'Client Management Software for Small Businesses',
    description: `Get clients faster with HoneyBook's all-in-one project, invoicing and payments management tool. Start your free 7-day trial today.`
};

interface Props {
    location: WindowLocation;
    pageContext: {
        isCtaTest: boolean;
    };
}

const PaidMarketingPage = ({ pageContext: { isCtaTest }, location }: Props) => {
    const { t } = useTranslation();
    const { promoCode } = usePromoCode();
    const path = location.pathname;
    const { values } = verticalPaidStore['generic'];

    const {
        variantPerTest: { broad_intent_get_started_hero: variant }
    } = useABTestContext();

    const content = useMemo(() => {
        if (
            variant === 'broad_intent_1_chat' ||
            variant === 'broad_intent_2_traditional'
        ) {
            return (
                <BroadIntentContent
                    variant={variant}
                    path={path}
                    promoCode={promoCode}
                />
            );
        }

        return (
            <div className="feature-block__wrapper">
                <FeatureChecklistHeroNew
                    module={
                        TAdditionalDataForClickStartTrialModules.CHECK_LIST_GET_STARTED_PAGE
                    }
                    title={t(`valuePropsHero.title`)}
                    increaseTitleFontSize
                />

                <RotatingTitleBlock />

                <FeatureValuePropsBlockNew values={values} minimal />

                <AiBlock />

                <TestimonialCarouselCardBlock isAltTheme />

                <OverlapCarouselBlock />

                <PromoBlockLeft
                    title={t(`${promoCode}.promoBlockLeft.title`)}
                    subtitle={t(`${promoCode}.promoBlockLeft.subtitle`, {
                        defaultValue: null
                    })}
                    promoCode={promoCode}
                    buttonStyle="primary--bright"
                    darkTheme
                    source="promo block left - dark"
                />
            </div>
        );
    }, [variant, t, promoCode]);

    // Track scroll event once per page view
    const hasFiredScrollEvent = useRef(false);
    useEffect(() => {
        const onScroll = () => {
            // Fire only when user reaches the bottom of the page
            const buffer = 2; // px, to account for floating point errors
            if (
                !hasFiredScrollEvent.current &&
                window.innerHeight + window.scrollY >=
                    document.body.offsetHeight - buffer
            ) {
                AnalyticsService.track(AnalyticsEvents.scroll_get_started, {
                    getstarted_glimpse_of_value_test: variant
                });
                hasFiredScrollEvent.current = true;
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [variant]);

    return (
        <div className={'paid-marketing'}>
            <Layout
                ogTags={ogTags}
                slimNav
                slimFooter
                mobileStickyCTA
                mobileStickyCTAOffset={500}
                isCtaTest={isCtaTest}
            >
                {content}
            </Layout>
        </div>
    );
};

export default PaidMarketingPage;

export const query = graphql`
    query ($language: String!) {
        locales: allLocale(
            filter: {
                ns: {
                    in: [
                        "common"
                        "paidMarketing"
                        "checklistHero"
                        "pricing"
                        "home"
                    ]
                }
                language: { eq: $language }
            }
        ) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
    }
`;
